/**
 * Shared contract for the plugin resource list pages.
 *
 * url-manager, pdf-manager, wysiwyg-manager and page-manager all expose the same server
 * API shape and render the same table, and each one used to re-declare these labels and
 * predicates in its own Angular controller. They live here once so a migrated page picks
 * them up instead of copying them again.
 */

// Verbatim from the Angular `statusLabel` filter, which every one of those pages defined
// identically.
export const STATUS_LABELS = {
  notimported: 'Not Imported',
  unpublished: 'Not Published',
  pending: 'Draft Pending',
  published: 'Published',
};

/**
 * Which pill each of those statuses wears (SURF-4808 step 2).
 *
 * This family showed status as plain grey text while the `itemTable` family showed a
 * coloured dot, so the same three states looked like two unrelated things depending on
 * which screen you were on. Both render the pill now. The labels above are unchanged:
 * they came verbatim from the Angular filter and say "Draft Pending" and "Not
 * Published" where the other family says "Pending" and "Unpublished".
 */
export const STATUS_TONES = {
  published: 'ok',
  pending: 'warn',
  unpublished: 'bad',
  notimported: 'neutral',
};

export const TAGS_MODAL_GROWL_ID = 'resourceTagModalGrowl';

/**
 * Only internal pages can be edited. External and orphaned rows are stripped copies owned
 * by the parent tenant: they have no local url or sandboxData, so an editable form would
 * show empty fields and a save would overwrite the row with blanks.
 */
export const canEditPage = (page) => !!page.isInternal;

// Publish-state predicates, verbatim from the Angular controllers. `isPublished` is set
// by the server from the live instance mapping, not derived from `status`.
export const isUnpublished = (page) => page.status === 'unpublished';
export const hasDraftPending = (page) => page.status === 'pending';
export const canPublish = (page) => isUnpublished(page) || hasDraftPending(page);
export const canUnpublish = (page) => !!page.isPublished;
export const canRemove = (page) => isUnpublished(page);

/**
 * SURF-4841: what a row becomes after a successful Publish or Unpublish, so the list can
 * change it in place instead of re-fetching (see `updateRow` in usePageTable).
 *
 * Neither route returns the page: pdf-manager and wysiwyg-manager both answer
 * `{ status: 'success' }` and nothing else, which is why the patch is built here rather
 * than taken from the response. The values are what the server's own status logic
 * (`setInternalPageStatus` / `setExternalPageStatus`) reports on the next fetch: publish
 * copies the draft to live, so the draft and the live copy match; unpublish drops the
 * live copy.
 */
export const ROW_AFTER_PUBLISH = { status: 'published', isPublished: true };
export const ROW_AFTER_UNPUBLISH = { status: 'unpublished', isPublished: false };

/** `rows` with the row whose id is `id` merged with `patch`; every other row as it was. */
export const patchRow = (rows, id, patch) =>
  (rows || []).map((row) => (row && row.id === id ? { ...row, ...patch } : row));

/**
 * Matches the Angular `date: 'MM/dd/yyyy h:mm a'` filter the tables used, in local time.
 */
export const formatResourceDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  let hours = date.getHours();
  const suffix = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear()} ${hours}:${pad(
    date.getMinutes()
  )} ${suffix}`;
};

/**
 * SURF-4803: only the newest table request may write its result.
 *
 * Changing the tag filter while the admin is past page one queues two fetches: one still
 * asking for the page they were on, and one for page one after the filter reset. The
 * first asks for, say, page four of a filter that only has one page, so it answers with
 * no rows. Whichever answered last used to win, so that empty answer could land second
 * and leave the table blank with a pager reading "1 of 1" and no way back.
 *
 * Each fetch takes a ticket; a ticket that is no longer the newest is ignored.
 */
export const createLatestRequestGate = () => {
  let issued = 0;
  return {
    take: () => {
      issued += 1;
      return issued;
    },
    isLatest: (ticket) => ticket === issued,
  };
};
